import type { Announcement, DesignSystem, Maintenance, MobileReleaseControl, OtaChannelName } from '@repo/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { platformService } from '@repo/ui/lib/api/services/platform.service';

/** Annonces à afficher pour l'utilisateur connecté (rafraîchies toutes les 5 minutes). */
export const useMyAnnouncements = () =>
  useQuery({
    queryKey: ['announcements', 'me'],
    queryFn: () => platformService.getMyAnnouncements(),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

/** État public de la plateforme ; `pollMs` pour attendre la fin d'une maintenance. */
export const usePlatformStatus = (pollMs?: number) =>
  useQuery({ queryKey: ['platform', 'status'], queryFn: () => platformService.getStatus(), refetchInterval: pollMs });

export const useAnnouncementsAdmin = () =>
  useQuery({ queryKey: ['superadmin', 'announcements'], queryFn: () => platformService.getAnnouncements() });

export const useMaintenanceAdmin = () =>
  useQuery({ queryKey: ['superadmin', 'maintenance'], queryFn: () => platformService.getMaintenance() });

export const usePlatformAdminActions = () => {
  const queryClient = useQueryClient();
  return {
    saveAnnouncements: useMutation({
      mutationFn: (announcements: Announcement[]) => platformService.updateAnnouncements(announcements),
      onSuccess: (announcements) => {
        queryClient.setQueryData(['superadmin', 'announcements'], announcements);
        void queryClient.invalidateQueries({ queryKey: ['announcements', 'me'] });
      },
    }),
    saveMaintenance: useMutation({
      mutationFn: (maintenance: Maintenance) => platformService.updateMaintenance(maintenance),
      onSuccess: (maintenance) => queryClient.setQueryData(['superadmin', 'maintenance'], maintenance),
    }),
  };
};

export const mobileReleasesQueryKey = ['superadmin', 'mobile-releases'] as const;

export const useMobileReleases = () =>
  useQuery({ queryKey: mobileReleasesQueryKey, queryFn: () => platformService.getMobileReleases() });

export const useUpdateMobileRelease = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ channel, control }: { channel: OtaChannelName; control: MobileReleaseControl }) =>
      platformService.updateMobileRelease(channel, control),
    onSuccess: (report) => queryClient.setQueryData(mobileReleasesQueryKey, report),
  });
};

export const useDesignSystemAdmin = () =>
  useQuery({ queryKey: ['superadmin', 'design-system'], queryFn: () => platformService.getDesignSystem() });

export const useUpdateDesignSystem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (designSystem: DesignSystem) => platformService.updateDesignSystem(designSystem),
    onSuccess: (designSystem) => {
      queryClient.setQueryData(['superadmin', 'design-system'], designSystem);
      void queryClient.invalidateQueries({ queryKey: ['platform', 'status'] });
    },
  });
};
